import React, { useMemo } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import {
  Table,
  Button,
  Tooltip,
  Icon,
  Card,
  Row,
  Col,
} from 'antd';
import Confirm from '../Confirm';

const tableStyles = `
@keyframes icon-pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
}
@keyframes fade-in {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes pulse-green {
  0%, 100% { box-shadow: 0 0 0 0 rgba(82,196,26,0.4); }
  50% { box-shadow: 0 0 0 4px rgba(82,196,26,0); }
}
@keyframes pulse-red {
  0%, 100% { box-shadow: 0 0 0 0 rgba(255,77,79,0.4); }
  50% { box-shadow: 0 0 0 4px rgba(255,77,79,0); }
}

.rr-table-container {
  animation: fade-in 0.4s ease both;
  position: relative;
  height: 100%;
}

.rr-action-btn {
  width: 28px !important;
  height: 28px !important;
  min-width: 28px !important;
  padding: 0 !important;
  border-radius: 7px !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  font-size: 13px !important;
  transition: all 0.2s ease !important;
  border: none !important;
}
.rr-action-btn:hover {
  transform: translateY(-2px) !important;
}
.rr-btn-delete {
  background: #fff1f0 !important;
  color: #ff4d4f !important;
}
.rr-btn-delete:hover {
  background: #ff4d4f !important;
  color: #fff !important;
  box-shadow: 0 4px 10px rgba(255,77,79,0.35) !important;
}
.rr-btn-print {
  background: #e6f7ff !important;
  color: #1890ff !important;
}
.rr-btn-print:hover {
  background: #1890ff !important;
  color: #fff !important;
  box-shadow: 0 4px 10px rgba(24,144,255,0.35) !important;
}
.rr-btn-rtv {
  background: #fff7e6 !important;
  color: #fa8c16 !important;
}
.rr-btn-rtv:hover {
  background: #fa8c16 !important;
  color: #fff !important;
  box-shadow: 0 4px 10px rgba(250,140,22,0.35) !important;
}

.mat-row-stripe td {
  background: #f7f9ff !important;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
}
.status-badge-no-reject {
  background: #f6ffed;
  color: #52c41a;
  border: 1px solid #b7eb8f;
}
.status-badge-with-reject {
  background: #fff1f0;
  color: #ff4d4f;
  border: 1px solid #ffccc7;
}
.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
}

/* Table wrapper styles */
.rr-table-wrapper {
  border-radius: 10px;
  border: 1px solid #e6f0ff;
  background: #fff;
  overflow: hidden;
  position: relative;
}

/* Fix for border alignment - ensures all column lines are aligned */
.rr-table-wrapper .ant-table {
  border-collapse: separate !important;
  border-spacing: 0 !important;
}

.rr-table-wrapper .ant-table-container {
  position: relative;
  border: none !important;
}

/* Sticky header implementation */
.rr-table-wrapper .ant-table-header {
  overflow-y: hidden !important;
}

.rr-table-wrapper .ant-table-body {
  overflow-x: auto !important;
  overflow-y: auto !important;
  max-height: calc(100vh - 450px);
}

/* Header styles - WITH #0984e3 COLOR - NOT BOLD */
.rr-table-wrapper .ant-table-thead > tr > th,
.rr-table-wrapper .ant-table-thead > tr > th.ant-table-cell,
.rr-table-wrapper .ant-table-thead tr th,
.rr-table-wrapper .ant-table-header .ant-table-thead th,
.ant-table-wrapper .rr-table-wrapper .ant-table-thead th {
  position: sticky !important;
  top: 0 !important;
  z-index: 100 !important;
  background: #0984e3 !important;
  background-color: #0984e3 !important;
  font-weight: 400 !important;
  padding: 12px 8px !important;
  border-bottom: 2px solid #0773c9 !important;
  border-right: 1px solid #2a95e6 !important;
  border-top: none !important;
  border-left: none !important;
  text-align: center !important;
  white-space: nowrap !important;
  word-break: keep-all !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  color: #ffffff !important;
  line-height: 1.4 !important;
  height: auto !important;
  font-size: 14px !important;
  letter-spacing: 0.3px !important;
}

/* Remove right border from last header cell */
.rr-table-wrapper .ant-table-thead > tr > th:last-child,
.rr-table-wrapper .ant-table-thead > tr > th.ant-table-cell:last-child {
  border-right: none !important;
}

/* Fixed left column headers */
.rr-table-wrapper .ant-table-fixed-left .ant-table-thead > tr > th,
.rr-table-wrapper .ant-table-fixed-left .ant-table-thead > tr > th.ant-table-cell {
  background: #0984e3 !important;
  background-color: #0984e3 !important;
  border-right: 1px solid #2a95e6 !important;
  border-bottom: 2px solid #0773c9 !important;
  font-weight: 400 !important;
}

/* Ensure fixed right column headers */
.rr-table-wrapper .ant-table-fixed-right .ant-table-thead > tr > th,
.rr-table-wrapper .ant-table-fixed-right .ant-table-thead > tr > th.ant-table-cell {
  background: #0984e3 !important;
  background-color: #0984e3 !important;
  border-left: 1px solid #2a95e6 !important;
  border-bottom: 2px solid #0773c9 !important;
  font-weight: 400 !important;
}

/* Header text styling - NOT BOLD */
.rr-table-wrapper .ant-table-thead > tr > th *,
.rr-table-wrapper .ant-table-thead > tr > th.ant-table-cell * {
  text-align: center !important;
  color: #ffffff !important;
  font-weight: 400 !important;
}

/* Header icons if any */
.rr-table-wrapper .ant-table-thead > tr > th .anticon,
.rr-table-wrapper .ant-table-thead > tr > th.ant-table-cell .anticon {
  color: #ffffff !important;
}

/* Sorting icons styling for blue header */
.rr-table-wrapper .ant-table-thead .ant-table-column-sorter {
  color: rgba(255, 255, 255, 0.7) !important;
}
.rr-table-wrapper .ant-table-thead .ant-table-column-sorter-up.active,
.rr-table-wrapper .ant-table-thead .ant-table-column-sorter-down.active {
  color: #ffffff !important;
}
.rr-table-wrapper .ant-table-thead .ant-table-column-sorter-up:hover,
.rr-table-wrapper .ant-table-thead .ant-table-column-sorter-down:hover {
  color: #ffffff !important;
}

/* Filter icon styling for blue header */
.rr-table-wrapper .ant-table-thead .ant-table-filter-trigger {
  color: rgba(255, 255, 255, 0.7) !important;
}
.rr-table-wrapper .ant-table-thead .ant-table-filter-trigger:hover,
.rr-table-wrapper .ant-table-thead .ant-table-filter-trigger.active {
  color: #ffffff !important;
  background: rgba(255, 255, 255, 0.15) !important;
}

/* Fix for cells to align properly with consistent borders */
.rr-table-wrapper .ant-table-tbody > tr > td {
  word-break: break-word !important;
  white-space: normal !important;
  word-wrap: break-word !important;
  overflow-wrap: break-word !important;
  max-width: 300px;
  line-height: 1.4;
  padding: 8px 12px !important;
  text-align: center !important;
  border-bottom: 1px solid #f0f0f0 !important;
  border-right: 1px solid #f0f0f0 !important;
  border-top: none !important;
  border-left: none !important;
}

/* Remove right border from last cell */
.rr-table-wrapper .ant-table-tbody > tr > td:last-child {
  border-right: none !important;
}

/* Ensure all columns are centered for body */
.rr-table-wrapper .ant-table-tbody > tr > td * {
  text-align: center !important;
}

/* Tooltip for truncated text if needed */
.rr-text-cell {
  word-break: break-word;
  white-space: normal;
  line-height: 1.4;
  max-height: 80px;
  overflow-y: auto;
  padding: 4px 0;
  text-align: center;
}

/* Ensure fixed columns stay on top when scrolling */
.rr-table-wrapper .ant-table-fixed-left {
  z-index: 99;
}
.rr-table-wrapper .ant-table-fixed-right {
  z-index: 99;
}

/* Fix for fixed column borders - ensures alignment */
.rr-table-wrapper .ant-table-fixed-left .ant-table-tbody > tr > td {
  border-right: 1px solid #f0f0f0 !important;
  border-left: none !important;
}

.rr-table-wrapper .ant-table-fixed-right .ant-table-tbody > tr > td {
  border-left: 1px solid #f0f0f0 !important;
  border-right: none !important;
}

/* Ensure first column has left border consistency */
.rr-table-wrapper .ant-table-fixed-left .ant-table-tbody > tr > td:first-child,
.rr-table-wrapper .ant-table-fixed-left .ant-table-thead > tr > th:first-child {
  border-left: none !important;
}

/* Scrollbar styling */
.rr-table-wrapper .ant-table-body::-webkit-scrollbar {
  height: 8px;
  width: 8px;
}
.rr-table-wrapper .ant-table-body::-webkit-scrollbar-track {
  background: #f0f0f0;
  border-radius: 4px;
}
.rr-table-wrapper .ant-table-body::-webkit-scrollbar-thumb {
  background: #c0c0c0;
  border-radius: 4px;
}
.rr-table-wrapper .ant-table-body::-webkit-scrollbar-thumb:hover {
  background: #a0a0a0;
}

/* Ensure table has consistent border */
.rr-table-wrapper .ant-table {
  border: none !important;
}

.rr-table-wrapper .ant-table-bordered .ant-table-container {
  border: none !important;
}

/* Statistics cards styling */
.rr-stats-card {
  border-radius: 10px;
  border: 1px solid #e6f0ff;
  background: linear-gradient(135deg, #fff, #fafcff);
  transition: all 0.3s ease;
  animation: fade-in 0.4s ease both;
}
.rr-stats-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(24,144,255,0.12);
}

.rr-stats-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}
.rr-stats-icon:hover {
  transform: scale(1.05);
}

/* Icon pulse animations - different delays for staggered effect */
.rr-stats-card:nth-child(1) .rr-stats-icon {
  animation: icon-pulse 1.6s ease-in-out infinite;
  animation-delay: 0s;
}
.rr-stats-card:nth-child(2) .rr-stats-icon {
  animation: icon-pulse 1.6s ease-in-out infinite;
  animation-delay: 0.3s;
}
.rr-stats-card:nth-child(3) .rr-stats-icon {
  animation: icon-pulse 1.6s ease-in-out infinite;
  animation-delay: 0.6s;
}
.rr-stats-card:nth-child(4) .rr-stats-icon {
  animation: icon-pulse 1.6s ease-in-out infinite;
  animation-delay: 0.9s;
}
`;

const ReceivingReportTable = (props) => {
  const {
    rrList, supplierOption, deleteReceivingReport, setLoading, printRrPDF, printRtvPDF,
  } = props;

  const stats = useMemo(() => {
    if (!rrList.length) return { total: 0, noReject: 0, withReject: 0, totalQuantity: 0 };
    const total = rrList.length;
    const noReject = rrList.filter(r => r.status === 'NO REJECT').length;
    const withReject = rrList.filter(r => r.status === 'W/ REJECT').length;
    const totalQuantity = rrList.reduce((sum, r) => sum + (r.quantity || 0), 0);
    return { total, noReject, withReject, totalQuantity };
  }, [rrList]);

  const columns = useMemo(() => [
    {
      key: 'removeRR',
      align: 'center',
      width: 60,
      fixed: 'left',
      render: (record) => (
        <Tooltip title="Delete record" placement="right">
          <Button
            size="small"
            icon="delete"
            onClick={() => Confirm(deleteReceivingReport, null, record.id, setLoading)}
            className="rr-action-btn rr-btn-delete"
          />
        </Tooltip>
      ),
    },
    {
      key: 'printRR',
      align: 'center',
      width: 60,
      fixed: 'left',
      render: (record) => (
        <Tooltip title="Print receiving report" placement="right">
          <Button
            size="small"
            icon="printer"
            onClick={() => printRrPDF(record.id, setLoading)}
            className="rr-action-btn rr-btn-print"
          />
        </Tooltip>
      ),
    },
    {
      key: 'printRTV',
      title: 'RTV',
      align: 'center',
      width: 70,
      fixed: 'left',
      render: (record) => {
        if (record.status === 'NO REJECT') {
          return <Icon type="stop" style={{ color: '#bfbfbf', fontSize: 16 }} />;
        }
        return (
          <Tooltip title="Print RTV" placement="right">
            <Button
              size="small"
              icon="printer"
              onClick={() => printRtvPDF(record.id, setLoading)}
              className="rr-action-btn rr-btn-rtv"
            />
          </Tooltip>
        );
      },
    },
    {
      key: 'status',
      title: 'STATUS',
      width: 120,
      render: (data) => {
        const isNoReject = data.status === 'NO REJECT';
        return (
          <div className={`status-badge ${isNoReject ? 'status-badge-no-reject' : 'status-badge-with-reject'}`}>
            <span className="status-dot" style={{ background: isNoReject ? '#52c41a' : '#ff4d4f' }} />
            {data.status}
          </div>
        );
      },
      align: 'center',
      filters: [
        { text: 'NO REJECT', value: 'NO REJECT' },
        { text: 'W/ REJECT', value: 'W/ REJECT' },
      ],
      onFilter: (value, record) => record.status === value,
      sorter: (a, b) => (a.status || '').localeCompare(b.status || ''),
    },
    {
      key: 'supplier',
      title: 'SUPPLIER',
      dataIndex: 'supplier',
      width: 200,
      align: 'center',
      filters: supplierOption.map((supplier) => ({
        text: supplier.supplierName,
        value: supplier.id,
      })),
      onFilter: (value, record) => record.supplierId === value,
      sorter: (a, b) => (a.supplier || '').localeCompare(b.supplier || ''),
      render: (text) => {
        if (!text) return '-';
        return (
          <Tooltip title={text} placement="topLeft">
            <div className="rr-text-cell">
              {text.toUpperCase()}
            </div>
          </Tooltip>
        );
      },
    },
    {
      key: 'rrNumber',
      title: 'RECEIVING REPORT',
      dataIndex: 'rrNumber',
      width: 180,
      align: 'center',
      sorter: (a, b) => (a.rrNumber || '').localeCompare(b.rrNumber || ''),
      render: (text) => {
        if (!text) return '-';
        return (
          <Tooltip title={text} placement="topLeft">
            <span style={{ fontWeight: 600, color: '#1890ff' }}>
              {text.toUpperCase()}
            </span>
          </Tooltip>
        );
      },
    },
    {
      key: 'poNum',
      title: 'PURCHASE ORDER',
      dataIndex: 'poNum',
      width: 150,
      align: 'center',
      sorter: (a, b) => (a.poNum || '').localeCompare(b.poNum || ''),
      render: (text) => {
        if (!text) return '-';
        return (
          <Tooltip title={text} placement="topLeft">
            <div className="rr-text-cell">
              {text}
            </div>
          </Tooltip>
        );
      },
    },
    {
      key: 'materialSpecification',
      title: 'MATERIAL SPECIFICATION',
      dataIndex: 'materialSpecification',
      width: 300,
      align: 'center',
      sorter: (a, b) => (a.materialSpecification || '').localeCompare(b.materialSpecification || ''),
      render: (text) => {
        if (!text) return '-';
        return (
          <Tooltip title={text} placement="topLeft">
            <div className="rr-text-cell">
              {text.toUpperCase()}
            </div>
          </Tooltip>
        );
      },
    },
    {
      key: 'invoice',
      title: 'INVOICE',
      dataIndex: 'invoice',
      width: 180,
      align: 'center',
      sorter: (a, b) => (a.invoice || '').localeCompare(b.invoice || ''),
      render: (text) => {
        if (!text) return '-';
        return (
          <Tooltip title={text} placement="topLeft">
            <div className="rr-text-cell">
              {text}
            </div>
          </Tooltip>
        );
      },
    },
    {
      key: 'dr',
      title: 'D.R NO.',
      dataIndex: 'dr',
      width: 180,
      align: 'center',
      sorter: (a, b) => (a.dr || '').localeCompare(b.dr || ''),
      render: (text) => {
        if (!text) return '-';
        return (
          <Tooltip title={text} placement="topLeft">
            <div className="rr-text-cell">
              {text}
            </div>
          </Tooltip>
        );
      },
    },
    {
      key: 'date',
      title: 'DATE',
      dataIndex: 'date',
      width: 120,
      align: 'center',
      sorter: (a, b) => moment(a.date).unix() - moment(b.date).unix(),
      render: (text) => text ? moment(text).format('MMM DD, YYYY') : '-',
    },
    {
      key: 'quantity',
      title: 'QTY',
      dataIndex: 'quantity',
      width: 80,
      align: 'center',
      sorter: (a, b) => (a.quantity || 0) - (b.quantity || 0),
      render: (text) => (text ? text.toLocaleString() : 0),
    },
    {
      key: 'inspectedQty',
      title: 'INSPECTED',
      dataIndex: 'inspectedQty',
      width: 100,
      align: 'center',
      sorter: (a, b) => (a.inspectedQty || 0) - (b.inspectedQty || 0),
      render: (text) => (text ? text.toLocaleString() : 0),
    },
    {
      key: 'receivedQty',
      title: 'RECEIVED',
      dataIndex: 'receivedQty',
      width: 100,
      sorter: (a, b) => (a.receivedQty || 0) - (b.receivedQty || 0),
      align: 'center',
      render: (text) => (text ? text.toLocaleString() : 0),
    },
    {
      key: 'remarks',
      title: 'REMARKS',
      dataIndex: 'remarks',
      width: 250,
      align: 'center',
      render: (text) => {
        if (!text) return '-';
        return (
          <Tooltip title={text} placement="topLeft">
            <div className="rr-text-cell">
              {text}
            </div>
          </Tooltip>
        );
      },
    },
  ], [supplierOption, deleteReceivingReport, setLoading, printRrPDF, printRtvPDF]);

  return (
    <div className="rr-table-container">
      <style>{tableStyles}</style>
      
      {/* Statistics Cards */}
      {rrList.length > 0 && (
        <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
          <Col xs={12} sm={12} md={8} lg={6}>
            <Card className="rr-stats-card" bodyStyle={{ padding: '16px' }} bordered={false}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div className="rr-stats-icon" style={{ background: '#e6f7ff' }}>
                  <Icon type="file-text" style={{ fontSize: 20, color: '#1890ff' }} />
                </div>
                <div>
                  <div style={{ fontSize: 11, color: '#8c8c8c', fontWeight: 600, letterSpacing: '0.3px' }}>
                    TOTAL RECORDS
                  </div>
                  <div style={{ fontSize: 28, fontWeight: 700, color: '#1890ff' }}>
                    {stats.total}
                  </div>
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={12} sm={12} md={8} lg={6}>
            <Card className="rr-stats-card" bodyStyle={{ padding: '16px' }} bordered={false}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div className="rr-stats-icon" style={{ background: '#f6ffed' }}>
                  <Icon type="check-circle" style={{ fontSize: 20, color: '#52c41a' }} />
                </div>
                <div>
                  <div style={{ fontSize: 11, color: '#8c8c8c', fontWeight: 600, letterSpacing: '0.3px' }}>
                    NO REJECT
                  </div>
                  <div style={{ fontSize: 28, fontWeight: 700, color: '#52c41a' }}>
                    {stats.noReject}
                  </div>
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={12} sm={12} md={8} lg={6}>
            <Card className="rr-stats-card" bodyStyle={{ padding: '16px' }} bordered={false}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div className="rr-stats-icon" style={{ background: '#fff1f0' }}>
                  <Icon type="warning" style={{ fontSize: 20, color: '#ff4d4f' }} />
                </div>
                <div>
                  <div style={{ fontSize: 11, color: '#8c8c8c', fontWeight: 600, letterSpacing: '0.3px' }}>
                    WITH REJECT
                  </div>
                  <div style={{ fontSize: 28, fontWeight: 700, color: '#ff4d4f' }}>
                    {stats.withReject}
                  </div>
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={12} sm={12} md={8} lg={6}>
            <Card className="rr-stats-card" bodyStyle={{ padding: '16px' }} bordered={false}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div className="rr-stats-icon" style={{ background: '#fff7e6' }}>
                  <Icon type="shopping-cart" style={{ fontSize: 20, color: '#fa8c16' }} />
                </div>
                <div>
                  <div style={{ fontSize: 11, color: '#8c8c8c', fontWeight: 600, letterSpacing: '0.3px' }}>
                    TOTAL QUANTITY
                  </div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: '#fa8c16' }}>
                    {stats.totalQuantity.toLocaleString()}
                  </div>
                </div>
              </div>
            </Card>
          </Col>
        </Row>
      )}
      
      {/* Table with Sticky Header and Horizontal Scroll */}
      <div className="rr-table-wrapper">
        <Table
          size="small"
          dataSource={rrList}
          columns={columns}
          scroll={{ 
            x: 'max-content',
            y: 'calc(100vh - 450px)'
          }}
          pagination={{
            showSizeChanger: true,
            pageSizeOptions: ['10', '25', '50', '100', '500'],
            showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total} items`,
            position: 'both',
          }}
          rowKey="id"
          bordered
          rowClassName={(_, index) => index % 2 === 0 ? '' : 'mat-row-stripe'}
        />
      </div>
      
      {rrList.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          background: '#fafafa',
          borderRadius: 10,
          marginTop: 20,
        }}>
          <Icon type="inbox" style={{ fontSize: 48, color: '#bfbfbf', marginBottom: 16 }} />
          <div style={{ fontSize: 14, color: '#8c8c8c' }}>No receiving reports found</div>
        </div>
      )}
    </div>
  );
};

ReceivingReportTable.propTypes = {
  rrList: PropTypes.arrayOf(PropTypes.object).isRequired,
  supplierOption: PropTypes.arrayOf(PropTypes.object).isRequired,
  deleteReceivingReport: PropTypes.func.isRequired,
  setLoading: PropTypes.func.isRequired,
  printRrPDF: PropTypes.func.isRequired,
  printRtvPDF: PropTypes.func.isRequired,
};

export default ReceivingReportTable;